import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Bookmark, Search, Share2, Sparkles, Star, UserCheck, Users } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MATERIAL_KINDS, listMaterials } from "@/lib/teacher-content.functions";
import { CommunityMaterialCard } from "@/components/CommunityMaterialCard";
import { COMMUNITY_SORTS, type CommunitySort, type SharedMaterial } from "@/lib/teacher-community";
import {
  browseSharedMaterials,
  getCommunityStats,
  listParentLinks,
  requestParentLink,
  respondParentLink,
  shareMaterial,
} from "@/lib/teacher-community.functions";

export const Route = createFileRoute("/_app/teacher-hub")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Community docenti e area genitore — Lumen" },
      {
        name: "description",
        content: "Cerca e riusa materiali condivisi dai colleghi, condividi i tuoi e collega i genitori ai progressi degli studenti.",
      },
      { property: "og:title", content: "Community docenti — Lumen" },
      { property: "og:description", content: "Materiali condivisi tra insegnanti e area genitore in sola lettura." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: TeacherHub,
});

type Shared = SharedMaterial;
type MyMaterial = { id: string; title: string; subject: string; kind: string; is_public?: boolean };

function TeacherHub() {
  const { t } = useTranslation();
  const browseFn = useServerFn(browseSharedMaterials);
  const statsFn = useServerFn(getCommunityStats);
  const shareFn = useServerFn(shareMaterial);
  const myFn = useServerFn(listMaterials);
  const linksFn = useServerFn(listParentLinks);
  const requestFn = useServerFn(requestParentLink);
  const respondFn = useServerFn(respondParentLink);

  const [filters, setFilters] = useState({ query: "", subject: "", kind: "", level: "" });
  const [sort, setSort] = useState<CommunitySort>("recent");
  const [savedOnly, setSavedOnly] = useState(false);
  const [followingOnly, setFollowingOnly] = useState(false);
  const [stats, setStats] = useState<Awaited<ReturnType<typeof getCommunityStats>> | null>(null);
  const [results, setResults] = useState<Shared[]>([]);
  const [mineList, setMineList] = useState<MyMaterial[]>([]);
  const [links, setLinks] = useState<Awaited<ReturnType<typeof listParentLinks>> | null>(null);
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);

  const search = useCallback(async () => {
    setResults((await browseFn({ data: { ...filters, sort, savedOnly, followingOnly } })) as Shared[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, savedOnly, followingOnly]);

  const reloadStats = useCallback(async () => {
    try {
      setStats(await statsFn({ data: undefined } as never));
    } catch {
      setStats(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadMine = useCallback(async () => {
    setMineList((await myFn({ data: {} })) as MyMaterial[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadLinks = useCallback(async () => {
    setLinks(await linksFn({ data: undefined } as never));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void search();
    void reloadMine();
    void reloadLinks();
    void reloadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, savedOnly, followingOnly]);

  return (
    <main className="space-y-5 px-4 py-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">{t("teacher.share.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("teacher.share.subtitle")}</p>
      </header>

      <Tabs defaultValue="browse" className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="browse" className="min-w-0 flex-1 truncate">
            {t("teacher.share.title")}
          </TabsTrigger>
          <TabsTrigger value="mine" className="min-w-0 flex-1 truncate">
            {t("teacher.studio.library")}
          </TabsTrigger>
          <TabsTrigger value="parent" className="min-w-0 flex-1 truncate">
            {t("teacher.parent.title")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="space-y-3 pt-3">
          <Card className="card-soft space-y-3 border-0 p-4">
            <div className="space-y-1.5">
              <Label htmlFor="s-q">{t("teacher.share.search")}</Label>
              <Input id="s-q" value={filters.query} onChange={(e) => setFilters({ ...filters, query: e.target.value })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="s-sub">{t("teacher.share.filterSubject")}</Label>
                <Input id="s-sub" value={filters.subject} onChange={(e) => setFilters({ ...filters, subject: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="s-kind">{t("teacher.share.filterKind")}</Label>
                <select
                  id="s-kind"
                  value={filters.kind}
                  onChange={(e) => setFilters({ ...filters, kind: e.target.value })}
                  className="h-11 w-full rounded-xl border border-input bg-background px-3 text-sm"
                >
                  <option value="">{t("teacher.share.any")}</option>
                  {MATERIAL_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`teacher.studio.kinds.${k}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Button className="min-h-11 w-full" onClick={() => void search()}>
              <Search className="mr-2 h-4 w-4" aria-hidden />
              {t("teacher.share.search")}
            </Button>
          </Card>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-xl bg-muted p-1" role="tablist" aria-label={t("teacher.share.sort")}>
              {COMMUNITY_SORTS.map((sKey) => (
                <button
                  key={sKey}
                  type="button"
                  role="tab"
                  aria-selected={sort === sKey}
                  onClick={() => setSort(sKey)}
                  className={`min-h-9 rounded-lg px-3 text-xs font-medium transition ${
                    sort === sKey ? "bg-background shadow-sm" : "text-muted-foreground"
                  }`}
                >
                  {t(`teacher.share.sorts.${sKey}`)}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              variant={savedOnly ? "secondary" : "outline"}
              className="min-h-9"
              aria-pressed={savedOnly}
              onClick={() => setSavedOnly((v) => !v)}
            >
              <Bookmark className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {t("teacher.share.onlySaved")}
            </Button>
            <Button
              size="sm"
              variant={followingOnly ? "secondary" : "outline"}
              className="min-h-9"
              aria-pressed={followingOnly}
              onClick={() => setFollowingOnly((v) => !v)}
            >
              <UserCheck className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {t("teacher.share.onlyFollowing")}
            </Button>
          </div>

          {results.length === 0 ? (
            <Card className="card-soft border-0 p-5 text-sm text-muted-foreground">{t("teacher.share.empty")}</Card>
          ) : (
            results.map((r, i) => (
              <CommunityMaterialCard
                key={r.id}
                material={r}
                onChange={(patch) =>
                  setResults((list) => list.map((x, j) => (j === i ? { ...x, ...patch } : x)))
                }
                onCopied={() => {
                  void reloadMine();
                  void reloadStats();
                }}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="mine" className="space-y-3 pt-3">
          {stats && (
            <Card className="card-soft grid grid-cols-2 gap-3 border-0 p-4 sm:grid-cols-4">
              <Stat icon={<Share2 className="h-3.5 w-3.5" aria-hidden />} label={t("teacher.share.stats.shared")} value={stats.shared} />
              <Stat icon={<Sparkles className="h-3.5 w-3.5" aria-hidden />} label={t("teacher.share.stats.copies")} value={stats.copies} />
              <Stat icon={<Star className="h-3.5 w-3.5" aria-hidden />} label={t("teacher.share.stats.rating")} value={stats.avgRating || "—"} />
              <Stat icon={<Users className="h-3.5 w-3.5" aria-hidden />} label={t("teacher.share.stats.followers")} value={stats.followers} />
            </Card>
          )}
          {mineList.length === 0 ? (
            <Card className="card-soft space-y-3 border-0 p-5">
              <p className="text-sm text-muted-foreground">{t("teacher.studio.empty")}</p>
              <Button asChild className="min-h-11 w-full">
                <Link to="/teaching-studio">{t("teacher.studio.title")}</Link>
              </Button>
            </Card>
          ) : (
            mineList.map((m) => (
              <Card key={m.id} className="card-soft flex flex-wrap items-center justify-between gap-3 border-0 p-4">
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-semibold leading-snug">{m.title}</p>
                  <p className="break-words text-xs text-muted-foreground">
                    {m.subject} · {m.is_public ? t("teacher.share.public") : t("teacher.share.private")}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Share2 className="h-4 w-4 text-muted-foreground" aria-hidden />
                  <Switch
                    checked={!!m.is_public}
                    aria-label={t("teacher.share.makePublic")}
                    onCheckedChange={async (v) => {
                      setMineList((l) => l.map((x) => (x.id === m.id ? { ...x, is_public: v } : x)));
                      try {
                        await shareFn({ data: { materialId: m.id, share: v } });
                      } catch {
                        setMineList((l) => l.map((x) => (x.id === m.id ? { ...x, is_public: !v } : x)));
                        toast.error(t("states.error.title", { defaultValue: "Something went wrong" }));
                      }
                    }}
                  />
                </div>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="parent" className="space-y-3 pt-3">
          <Card className="card-soft space-y-3 border-0 p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <Users className="h-4 w-4" aria-hidden />
              {t("teacher.parent.link")}
            </h2>
            <p className="text-xs text-muted-foreground">{t("teacher.parent.linkHint")}</p>
            <div className="space-y-1.5">
              <Label htmlFor="p-user">{t("teacher.parentArea.linkStudent")}</Label>
              <Input
                id="p-user"
                value={username}
                placeholder={t("teacher.parentArea.linkPh")}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <Button
              className="min-h-11 w-full"
              disabled={busy || username.trim().length < 2}
              onClick={async () => {
                setBusy(true);
                try {
                  await requestFn({ data: { username: username.trim() } });
                  toast.success(t("teacher.parentArea.sent"));
                  setUsername("");
                  void reloadLinks();
                } catch {
                  toast.error(t("teacher.parentArea.notFound"));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t("teacher.parentArea.send")}
            </Button>
          </Card>

          <Card className="card-soft space-y-2 border-0 p-4">
            <h2 className="text-sm font-semibold">{t("teacher.parentArea.children")}</h2>
            {!links || links.children.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("teacher.parent.empty")}</p>
            ) : (
              links.children.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-muted/40 p-3">
                  <div className="min-w-0">
                    <p className="break-words text-sm font-medium">{c.name}</p>
                    <p className="break-words text-xs text-muted-foreground">
                      {c.status === "accepted"
                        ? t("teacher.parentArea.stats", { level: c.level, xp: c.xp, streak: c.streak })
                        : t("teacher.parent.pending")}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="min-h-10 shrink-0 text-destructive"
                    onClick={async () => {
                      await respondFn({ data: { linkId: c.id, accept: false } });
                      void reloadLinks();
                    }}
                  >
                    {t("teacher.parentArea.revoke")}
                  </Button>
                </div>
              ))
            )}
          </Card>

          <Card className="card-soft space-y-2 border-0 p-4">
            <h2 className="text-sm font-semibold">{t("teacher.parentArea.requests")}</h2>
            {!links || links.requests.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("teacher.parentArea.noRequests")}</p>
            ) : (
              links.requests.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-muted/40 p-3">
                  <div className="min-w-0">
                    <p className="break-words text-sm font-medium">{r.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.status === "accepted" ? t("teacher.parent.accepted") : t("teacher.parent.pending")}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {r.status !== "accepted" && (
                      <Button
                        size="sm"
                        className="min-h-10"
                        onClick={async () => {
                          await respondFn({ data: { linkId: r.id, accept: true } });
                          void reloadLinks();
                        }}
                      >
                        {t("teacher.parentArea.accept")}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="min-h-10 text-destructive"
                      onClick={async () => {
                        await respondFn({ data: { linkId: r.id, accept: false } });
                        void reloadLinks();
                      }}
                    >
                      {r.status === "accepted" ? t("teacher.parentArea.revoke") : t("teacher.parentArea.decline")}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}

function Stat({ icon, label, value }: { icon: ReactNode; label: string; value: number | string }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
